/* ==========================================================================
   BRAND STORY
   Three independent pieces:
     1. Thread fill - grows with scroll position (a progress cue, not an
        entrance animation, so reduced-motion just shows it fully lit).
     2. Chapter reveal - a dedicated two-way IntersectionObserver (appears
        entering the viewport, fades back out leaving it either direction).
        Deliberately not the sitewide one-shot .reveal/.in from theme.js,
        which never hides again once shown.
     3. Node steam - a subtle WebGL wisp drifting upward behind each
        chapter's diamond, lazily created and only rendering while that
        diamond is hovered, so four idle WebGL contexts aren't animating
        at once.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-brand-story]');
  if (!root) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ======================================================================
     1. THREAD FILL + SCROLL CUE
     ====================================================================== */
  var fill = root.querySelector('[data-thread-fill]');
  var scrollCue = root.querySelector('[data-scroll-cue]');
  var threadTip = root.querySelector('[data-thread-tip]');
  if (REDUCED) {
    if (fill) fill.style.height = '100%';
    if (scrollCue) scrollCue.classList.add('is-hidden');
    if (threadTip) threadTip.classList.add('is-hidden');
  } else {
    var fillTicking = false;
    function updateFill() {
      fillTicking = false;
      var rect = root.getBoundingClientRect();
      var anchor = window.innerHeight * 0.55;
      var progress = (anchor - rect.top) / rect.height;
      progress = Math.max(0, Math.min(1, progress));
      if (fill) fill.style.height = (progress * 100) + '%';
      var pastScrollStart = window.scrollY > 60;
      if (scrollCue) {
        // Same percentage basis as the fill (both live in the thread's own
        // box), so the cue always sits right at the tip of the lit portion.
        scrollCue.style.top = (progress * 100) + '%';
        scrollCue.classList.toggle('is-hidden', pastScrollStart);
      }
      if (threadTip) {
        threadTip.style.top = (progress * 100) + '%';
        threadTip.classList.toggle('is-hidden', pastScrollStart);
      }
    }
    window.addEventListener('scroll', function () {
      if (!fillTicking) { fillTicking = true; requestAnimationFrame(updateFill); }
    }, { passive: true });
    window.addEventListener('resize', updateFill);
    updateFill();
  }

  /* ======================================================================
     2. CHAPTER REVEAL (two-way)
     ====================================================================== */
  var chapters = Array.prototype.slice.call(root.querySelectorAll('[data-story-chapter]'));
  // Suppressed for a moment after a click-triggered scroll (below), so the
  // chapter being scrolled away from doesn't flip .is-visible off - and
  // trigger its own hide-transition - while it's mid-flight past the
  // intersection threshold. That fight is what caused the "shake".
  var suppressReveal = false;
  if (REDUCED || !('IntersectionObserver' in window)) {
    chapters.forEach(function (el) { el.classList.add('is-visible'); });
  } else {
    // Hide should only ever happen scrolling back up past something already
    // read - not while scrolling down through a chapter taller than the
    // viewport (which would otherwise dip below the visibility threshold
    // just from continuing to scroll forward through its own text).
    var lastScrollY = window.scrollY;
    var scrollingUp = false;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      scrollingUp = y < lastScrollY;
      lastScrollY = y;
    }, { passive: true });

    // Two independent observers rather than one shared threshold: reveal
    // and hide need different trigger points (hide noticeably quicker than
    // reveal), which a single boundary can't express since entering and
    // leaving cross the very same line.
    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    var hideIO = new IntersectionObserver(function (entries) {
      if (suppressReveal || !scrollingUp) return;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) entry.target.classList.remove('is-visible');
      });
    }, { threshold: 0.85 });

    chapters.forEach(function (el) { revealIO.observe(el); hideIO.observe(el); });
  }

  /* ======================================================================
     NODE CLICK -> jump to the next chapter
     Independent of the WebGL steam effect below, so it works even without
     WebGL or with reduced motion (just skips the smooth-scroll animation).
     ====================================================================== */
  var suppressTimer = null;
  root.querySelectorAll('[data-story-node]').forEach(function (node, i) {
    node.addEventListener('click', function () {
      var next = chapters[i + 1];
      if (!next) return;
      suppressReveal = true;
      clearTimeout(suppressTimer);
      suppressTimer = setTimeout(function () { suppressReveal = false; }, 900);
      next.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
    });
  });

  /* ======================================================================
     3. NODE STEAM (WebGL, hover-only)
     Deferred to 'load'. This section loads three.min.js with 'defer'
     immediately before this file's own script tag, so THREE is normally
     already defined by the time this file executes - but the
     `typeof THREE === 'undefined'` guard stays in place in case that asset
     fails to fetch.
     ====================================================================== */
  if (REDUCED) return;

  function initNodeSteam() {
    if (typeof THREE === 'undefined') return;

    var STEAM_VERT = 'varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,0.0,1.0); }';
    // Soft wisps of "steam" drifting upward behind the diamond - low-alpha,
    // domain-warped noise masked to a loose vertical column, fading at its
    // own top/bottom/sides rather than a bright radiating burst.
    var STEAM_FRAG = [
      'precision highp float;',
      'varying vec2 vUv;',
      'uniform float uTime; uniform vec3 uColor;',
      'float hash(vec2 p){ p=fract(p*vec2(234.34,435.345)); p+=dot(p,p+34.23); return fract(p.x*p.y); }',
      'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);',
      '  return mix(mix(hash(i),hash(i+vec2(1.,0.)),u.x), mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),u.x),u.y); }',
      'float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<4;i++){ v+=a*noise(p); p*=2.0; a*=0.5; } return v; }',
      'void main(){',
      '  vec2 p = (vUv - 0.5) * vec2(2.2, 3.2);',
      '  float rise = uTime * 0.26;',
      // Sway grows with height - steam is tight near the source and wafts
      // further off-course the higher (and further from the diamond) it gets.
      '  float swayStrength = vUv.y * 0.55;',
      '  float sway = (sin(uTime*0.65 + vUv.y*5.0) * 0.5 + (noise(vec2(vUv.y*2.5, uTime*0.3)) - 0.5)) * swayStrength;',
      '  float turbulence = 1.0 + vUv.y * 0.8;',
      '  vec2 warp = vec2(fbm(p*1.1 + vec2(0.0, rise)), fbm(p*1.1 + vec2(3.1, rise*0.8)));',
      '  float n = fbm(p*1.6 + warp*1.2*turbulence - vec2(sway*2.2, rise*1.3));',
      // Mostly a thin drifting thread - "puff" is a sharply-shaped pulse
      // (high power on a sine) so it sits near zero most of the time with
      // occasional bursts, briefly widening and thickening the thread.
      '  float puff = pow(sin(uTime*0.45) * 0.5 + 0.5, 5.0);',
      '  float threadWidth = mix(0.3, 0.62, puff);',
      '  float xPos = (vUv.x - 0.5) + sway;',
      '  float colMask = smoothstep(threadWidth, 0.0, abs(xPos) * 2.0);',
      '  float vMask = smoothstep(0.0, 0.22, vUv.y) * smoothstep(1.0, 0.6, vUv.y);',
      '  float mask = colMask * vMask;',
      '  float wisp = smoothstep(0.3, 0.68, n);',
      // Pale, whitish steam colour - just a whisper of the chapter accent
      // rather than the fully saturated brand gold/rose.
      '  vec3 steamColor = mix(vec3(0.86, 0.84, 0.8), uColor, 0.16);',
      '  vec3 col = steamColor * (0.85 + n*0.3);',
      '  float density = mix(0.35, 1.0, puff);',
      '  gl_FragColor = vec4(col, mask * (0.06 + wisp * 0.4) * density);',
      '}'
    ].join('\n');

    function hexToVec3(hex) {
      var m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
      var v = m ? parseInt(m[1], 16) : 0xc9a15e;
      return new THREE.Vector3(((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255);
    }

    function createNodeGlow(canvas, colorHex) {
      var renderer;
      try {
        renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
      } catch (err) {
        return null;
      }
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      var w = canvas.clientWidth || 90, h = canvas.clientHeight || 120;
      renderer.setSize(w, h, false);

      var scene = new THREE.Scene();
      var camera = new THREE.Camera();
      var steamMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uColor: { value: hexToVec3(colorHex) } },
        vertexShader: STEAM_VERT, fragmentShader: STEAM_FRAG, transparent: true
      });
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), steamMat));

      var running = false, rafId = null, start = null;
      function frame(ts) {
        if (!running) return;
        if (start === null) start = ts;
        steamMat.uniforms.uTime.value = (ts - start) * 0.001;
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(frame);
      }
      return {
        start: function () { if (running) return; running = true; start = null; rafId = requestAnimationFrame(frame); },
        stop: function () { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }
      };
    }

    root.querySelectorAll('[data-story-node]').forEach(function (node) {
      var canvas = node.querySelector('[data-node-canvas]');
      if (!canvas) return;
      var chapter = node.closest('[data-story-chapter]');
      var accent = chapter ? getComputedStyle(chapter).getPropertyValue('--chapter-accent') : '#c9a15e';
      var glow = null;

      function engage() {
        if (!glow) glow = createNodeGlow(canvas, accent);
        if (!glow) return;
        canvas.classList.add('is-active');
        glow.start();
      }
      function disengage() {
        canvas.classList.remove('is-active');
        if (glow) glow.stop();
      }
      node.addEventListener('mouseenter', engage);
      node.addEventListener('mouseleave', disengage);
    });
  }

  if (document.readyState === 'complete') { initNodeSteam(); }
  else { window.addEventListener('load', initNodeSteam); }
})();
